import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BottomNav from "../components/BottomNav";
import API from "../services/api";
import { ArrowLeft, Bell, BellOff, Building2, Camera, ChevronRight, CircleHelp, Copy, Gift, LogOut, Phone, Share2, Wallet } from "lucide-react";
import WhatsAppGlyph from "../components/WhatsAppGlyph";
import { buildWhatsAppUrl, openExternalLink, shareAppLink } from "../utils/appInteractions";
import { getMediaUrl } from "../services/api";

function Profile() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [settings, setSettings] = useState({ whatsapp_number: "" });
  const [assignedAgent, setAssignedAgent] = useState(null);
  const [notificationPrefs, setNotificationPrefs] = useState({ result_notifications_enabled: true });
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [profileUser, setProfileUser] = useState(user);
  const [photoUploading, setPhotoUploading] = useState(false);
  const joinedDate = useMemo(() => {
    const raw = profileUser?.created_at || profileUser?.joined_at || profileUser?.date || null;
    const date = raw ? new Date(raw) : new Date();
    return Number.isNaN(date.getTime())
      ? "-"
      : date.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  }, [profileUser]);

  useEffect(() => {
    if (!user?.id) {
      navigate("/login");
      return;
    }

    API.get(`/wallet/${user.id}`)
      .then((res) => setWallet(res.data || { balance: 0 }))
      .catch(() => setWallet({ balance: 0 }));

    API.get("/settings/public")
      .then((res) => setSettings(res.data || {}))
      .catch(() => setSettings({ whatsapp_number: "" }));

    API.get(`/users/${user.id}/assigned-agent`)
      .then((res) => setAssignedAgent(res.data?.has_agent ? res.data.agent : null))
      .catch(() => setAssignedAgent(null));

    API.get(`/users/${user.id}/notification-preferences`)
      .then((res) => setNotificationPrefs({
        result_notifications_enabled: res.data?.result_notifications_enabled !== false,
      }))
      .catch(() => setNotificationPrefs({ result_notifications_enabled: true }));
  }, [navigate, user]);

  const logout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("mk_user_session_active");
    navigate("/login");
  };

  const uploadProfilePhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !profileUser?.id) return;

    const formData = new FormData();
    formData.append("photo", file);
    try {
      setPhotoUploading(true);
      const res = await API.post(`/users/${profileUser.id}/profile-photo`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const nextUser = res.data?.user || { ...profileUser, profile_image: res.data?.profile_image };
      localStorage.setItem("user", JSON.stringify(nextUser));
      setProfileUser(nextUser);
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to update profile photo");
    } finally {
      setPhotoUploading(false);
    }
  };

  const openWhatsapp = () => {
    const phone = getWhatsappPhone(settings.whatsapp_number);
    if (!phone) return;
    const url = buildWhatsAppUrl(phone, "Hello Matka Kings support");
    if (!url) return;
    openExternalLink(url);
  };

  const openAgentWhatsapp = () => {
    const phone = getWhatsappPhone(assignedAgent?.whatsapp_number);
    if (!phone) {
      alert("Agent WhatsApp number is not available");
      return;
    }
    const agentName = assignedAgent?.display_name || "Agent";
    const url = buildWhatsAppUrl(phone, `Hello ${agentName}`);
    if (!url) return;
    openExternalLink(url);
  };

  const copyUserId = async () => {
    try {
      await navigator.clipboard.writeText(String(user?.id || ""));
      alert("User ID copied");
    } catch (_) {}
  };

  const shareApp = async () => {
    const referralCode = String(user?.referral_code || "").trim();
    const url = `${window.location.origin}/login?tab=register${referralCode ? `&ref=${encodeURIComponent(referralCode)}` : ""}`;
    const text = referralCode
      ? `Join Matka Kings with my referral code ${referralCode}`
      : "Join Matka Kings";

    try {
      const shared = await shareAppLink({ title: "Matka Kings", text, url });
      if (!shared) alert("Share link copied");
    } catch (_) {}
  };

  const toggleResultNotifications = async () => {
    if (!user?.id || savingNotifications) return;
    const nextValue = !notificationPrefs.result_notifications_enabled;
    setNotificationPrefs({ result_notifications_enabled: nextValue });
    setSavingNotifications(true);
    try {
      const res = await API.put(`/users/${user.id}/notification-preferences`, {
        result_notifications_enabled: nextValue,
      });
      setNotificationPrefs({
        result_notifications_enabled: res.data?.result_notifications_enabled !== false,
      });
    } catch (err) {
      setNotificationPrefs({ result_notifications_enabled: !nextValue });
      alert(err?.response?.data?.message || "Unable to update notification setting");
    } finally {
      setSavingNotifications(false);
    }
  };

  return (
    <div style={page}>
      <header style={header}>
        <button type="button" style={backButton} onClick={() => navigate("/dashboard")}>
          <ArrowLeft size={24} />
        </button>
        <strong style={headerTitle}>My Profile</strong>
      </header>

      <main style={content}>
        <section style={summaryCard}>
          <div style={summaryTop}>
            <div style={summaryGlow} />
            <div style={summaryShine} />
            <div style={identityRow}>
              <label style={avatar}>
                {profileUser?.profile_image ? (
                  <img src={getMediaUrl(profileUser.profile_image)} alt="" style={avatarImage} />
                ) : (
                  profileUser?.name?.charAt(0)?.toUpperCase() || "U"
                )}
                <span style={cameraBadge}>{photoUploading ? "..." : <Camera size={13} />}</span>
                <input type="file" accept="image/*" style={hiddenInput} onChange={uploadProfilePhoto} disabled={photoUploading} />
              </label>
              <div style={{ minWidth: 0 }}>
                <div style={userName}>{profileUser?.name || "User"}</div>
                <button type="button" style={userMetaButton} onClick={copyUserId}>
                  <span>{`ID: ${profileUser?.id || "-"}`}</span>
                  <Copy size={14} />
                </button>
              </div>
            </div>
          </div>
          <div style={summaryStats}>
            <div style={summaryStat}>
              <div style={summaryLabel}>Balance</div>
              <div style={summaryValue}>{`Rs ${Number(wallet.balance || 0).toFixed(2)}`}</div>
            </div>
            <div style={summaryDivider} />
            <div style={summaryStat}>
              <div style={summaryLabel}>Joined</div>
              <div style={summaryDate}>{joinedDate}</div>
            </div>
          </div>
        </section>

        <section style={infoSection}>
          <div style={sectionTitle}>Account Info</div>
          <div style={infoCard}>
            <InfoRow icon={<Phone size={20} />} iconBg="#ecfdf5" iconColor="#059669" label="Phone" value={profileUser?.phone || "-"} />
          </div>
        </section>

        <section style={menuCard}>
          <Action icon={<Wallet size={20} />} iconBg="#316ce8" title="Wallet" subtitle="Manage deposits & withdrawals" onClick={() => navigate("/wallet")} />
          {assignedAgent ? (
            <PersonalAgentAction
              name={assignedAgent.display_name || "Agent"}
              onClick={openAgentWhatsapp}
            />
          ) : null}
          <Action icon={<Building2 size={20} />} iconBg="#111827" title="Bank Accounts" subtitle="Manage withdrawal details" onClick={() => navigate("/bank-accounts")} />
          <Action icon={<Gift size={20} />} iconBg="#ea580c" title="Refer & Earn" subtitle="Invite friends, earn bonus" onClick={() => navigate("/refer")} />
          <Action icon={<CircleHelp size={20} />} iconBg="#7c3aed" title="Game Rules" subtitle="Betting, timing & withdraw rules" onClick={() => navigate("/game-rules")} />
          <ToggleAction
            icon={notificationPrefs.result_notifications_enabled ? <Bell size={20} /> : <BellOff size={20} />}
            iconBg={notificationPrefs.result_notifications_enabled ? "#2563eb" : "#64748b"}
            title="Result Notifications"
            subtitle={notificationPrefs.result_notifications_enabled ? "Result alerts are on" : "Result alerts are off"}
            checked={notificationPrefs.result_notifications_enabled}
            disabled={savingNotifications}
            onClick={toggleResultNotifications}
          />
          <Action icon={<WhatsAppGlyph size={20} color="#ffffff" />} iconBg="#17b857" title="Help & Support" subtitle="24/7 WhatsApp support" onClick={openWhatsapp} />
          <Action icon={<Share2 size={20} />} iconBg="#2563eb" title="Share App" subtitle="Send app link with your referral code" onClick={shareApp} />
        </section>

        <button type="button" style={logoutButton} onClick={logout}>
          <LogOut size={18} /> Logout
        </button>
      </main>

      <BottomNav />
    </div>
  );
}

function Action({ icon, iconBg, title, subtitle, onClick }) {
  return (
    <button type="button" style={actionRow} onClick={onClick}>
      <div style={{ ...actionIcon, background: iconBg }}>{icon}</div>
      <div style={{ flex: 1, textAlign: "left" }}>
        <div style={actionTitle}>{title}</div>
        <div style={actionSubtitle}>{subtitle}</div>
      </div>
      <ChevronRight size={20} color="#cbd5e1" />
    </button>
  );
}

function PersonalAgentAction({ name, onClick }) {
  return (
    <button type="button" style={personalAgentRow} onClick={onClick}>
      <div style={personalAgentIcon}><WhatsAppGlyph size={21} color="#ffffff" /></div>
      <div style={{ flex: 1, textAlign: "left" }}>
        <div style={personalAgentTitle}>Contact {name}</div>
        <div style={personalAgentSubtitle}>Your personal agent</div>
      </div>
      <ChevronRight size={20} color="#16a34a" />
    </button>
  );
}

function ToggleAction({ icon, iconBg, title, subtitle, checked, disabled, onClick }) {
  return (
    <button type="button" style={actionRow} onClick={onClick} disabled={disabled}>
      <div style={{ ...actionIcon, background: iconBg }}>{icon}</div>
      <div style={{ flex: 1, textAlign: "left" }}>
        <div style={actionTitle}>{title}</div>
        <div style={actionSubtitle}>{disabled ? "Saving..." : subtitle}</div>
      </div>
      <span style={checked ? switchOn : switchOff}>
        <span style={checked ? switchKnobOn : switchKnobOff} />
      </span>
    </button>
  );
}

function getWhatsappPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  return digits;
}

function InfoRow({ icon, iconBg, iconColor, label, value }) {
  return (
    <div style={infoRow}>
      <div style={{ ...infoIcon, background: iconBg, color: iconColor }}>{icon}</div>
      <div>
        <div style={infoLabel}>{label}</div>
        <div style={infoValue}>{value}</div>
      </div>
    </div>
  );
}

const page = { minHeight: "100vh", background: "#f8fafc", paddingBottom: "84px" };
const header = { height: "50px", background: "#fff", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: "12px", padding: "0 12px", position: "sticky", top: 0, zIndex: 10 };
const backButton = { width: "36px", height: "36px", border: 0, borderRadius: "50%", background: "#f8fafc", display: "grid", placeItems: "center" };
const headerTitle = { fontSize: "15px", fontWeight: 800, color: "#0f172a" };
const content = { maxWidth: "540px", margin: "0 auto", padding: "10px", display: "grid", gap: "10px" };
const summaryCard = { background: "#fff", borderRadius: "22px", overflow: "hidden", border: "1px solid #f5e6b6", boxShadow: "0 16px 34px rgba(180,83,9,0.14)" };
const summaryTop = { background: "radial-gradient(circle at 12% 0%, rgba(255,255,255,.36), transparent 34%), linear-gradient(135deg,#3f2300 0%,#b8750b 42%,#f8d66f 74%,#7c2d12 100%)", padding: "16px 14px 18px", position: "relative", overflow: "hidden" };
const summaryGlow = { position: "absolute", inset: "-45% -22% auto auto", width: "180px", height: "180px", borderRadius: "50%", background: "rgba(255,255,255,.22)", filter: "blur(10px)" };
const summaryShine = { position: "absolute", inset: 0, background: "linear-gradient(112deg, transparent 0%, rgba(255,255,255,.18) 38%, transparent 56%)", transform: "translateX(-18%)", opacity: 0.75 };
const identityRow = { display: "flex", alignItems: "center", gap: "12px", position: "relative", zIndex: 1 };
const avatar = { width: "58px", height: "58px", borderRadius: "20px", background: "linear-gradient(145deg,rgba(255,255,255,.34),rgba(255,255,255,.08))", border: "1px solid rgba(255,255,255,0.42)", display: "grid", placeItems: "center", fontSize: "25px", fontWeight: 900, color: "#fff", flexShrink: 0, boxShadow: "inset 0 1px 0 rgba(255,255,255,.34), 0 14px 24px rgba(0,0,0,.18)", position: "relative", overflow: "visible", cursor: "pointer" };
const avatarImage = { width: "100%", height: "100%", borderRadius: "20px", objectFit: "cover", display: "block" };
const cameraBadge = { position: "absolute", right: "-4px", bottom: "-4px", width: "24px", height: "24px", borderRadius: "999px", background: "#111827", color: "#fff", display: "grid", placeItems: "center", border: "2px solid rgba(255,255,255,.9)", boxShadow: "0 6px 12px rgba(0,0,0,.22)", fontSize: "10px" };
const hiddenInput = { position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" };
const userName = { fontSize: "20px", fontWeight: 900, color: "#fff", lineHeight: 1.08, wordBreak: "break-word", letterSpacing: "-.2px", textShadow: "0 2px 12px rgba(0,0,0,.22)" };
const userMetaButton = { marginTop: "6px", border: "1px solid rgba(255,255,255,.18)", background: "rgba(255,255,255,.08)", color: "#fff7d6", padding: "6px 10px", borderRadius: "999px", display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "11px", fontWeight: 800, boxShadow: "inset 0 1px 0 rgba(255,255,255,.12)" };
const summaryStats = { display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "stretch", background: "#fff" };
const summaryStat = { padding: "10px 12px", textAlign: "center" };
const summaryDivider = { width: "1px", background: "#f1f5f9" };
const summaryLabel = { color: "#94a3b8", textTransform: "uppercase", fontWeight: 800, fontSize: "9px", letterSpacing: "0.04em" };
const summaryValue = { marginTop: "5px", color: "#0f172a", fontSize: "17px", fontWeight: 1000, letterSpacing: "-.2px" };
const summaryDate = { marginTop: "5px", color: "#111827", fontSize: "12px", fontWeight: 800, lineHeight: 1.25 };
const infoSection = { display: "grid", gap: "6px" };
const sectionTitle = { color: "#94a3b8", textTransform: "uppercase", fontWeight: 900, fontSize: "10px", letterSpacing: "0.08em", paddingLeft: "4px" };
const infoCard = { background: "#fff", borderRadius: "16px", border: "1px solid #eceff5", overflow: "hidden" };
const infoRow = { display: "flex", alignItems: "center", gap: "10px", padding: "11px 12px", borderBottom: "1px solid #f1f5f9" };
const infoIcon = { width: "36px", height: "36px", borderRadius: "12px", display: "grid", placeItems: "center", flexShrink: 0 };
const infoLabel = { color: "#94a3b8", textTransform: "uppercase", fontWeight: 800, fontSize: "9px", letterSpacing: "0.05em" };
const infoValue = { marginTop: "2px", color: "#1e293b", fontSize: "12px", fontWeight: 700 };
const menuCard = { background: "#fff", borderRadius: "16px", border: "1px solid #e5e7eb", overflow: "hidden" };
const actionRow = { width: "100%", border: 0, borderBottom: "1px solid #f1f5f9", background: "#fff", display: "flex", alignItems: "center", gap: "10px", padding: "11px 12px" };
const actionIcon = { width: "36px", height: "36px", borderRadius: "12px", display: "grid", placeItems: "center", color: "#fff", flexShrink: 0, overflow: "hidden" };
const actionTitle = { fontWeight: 800, color: "#1e293b", fontSize: "13px" };
const actionSubtitle = { marginTop: "2px", color: "#94a3b8", fontSize: "10px" };
const personalAgentRow = { width: "100%", border: 0, borderBottom: "1px solid #bbf7d0", background: "linear-gradient(135deg,#ecfdf5 0%,#fff7ed 100%)", display: "flex", alignItems: "center", gap: "10px", padding: "12px" };
const personalAgentIcon = { width: "38px", height: "38px", borderRadius: "14px", display: "grid", placeItems: "center", color: "#fff", flexShrink: 0, overflow: "hidden", background: "linear-gradient(135deg,#16a34a,#22c55e)", boxShadow: "0 8px 18px rgba(22,163,74,.24)", border: "2px solid rgba(255,255,255,.8)" };
const personalAgentTitle = { fontWeight: 1000, color: "#14532d", fontSize: "14px", letterSpacing: "-.1px" };
const personalAgentSubtitle = { marginTop: "3px", color: "#b45309", fontSize: "11px", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".04em" };
const switchBase = { width: "44px", height: "25px", borderRadius: "999px", padding: "3px", display: "inline-flex", alignItems: "center", transition: "background .16s ease", flexShrink: 0 };
const switchOn = { ...switchBase, background: "linear-gradient(135deg,#22c55e,#16a34a)" };
const switchOff = { ...switchBase, background: "#cbd5e1" };
const switchKnobBase = { width: "19px", height: "19px", borderRadius: "50%", background: "#fff", boxShadow: "0 2px 5px rgba(15,23,42,.25)", transition: "transform .16s ease" };
const switchKnobOn = { ...switchKnobBase, transform: "translateX(19px)" };
const switchKnobOff = { ...switchKnobBase, transform: "translateX(0)" };
const logoutButton = { width: "100%", height: "42px", border: "1px solid #fecaca", borderRadius: "14px", background: "#fff", color: "#dc2626", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontSize: "13px" };

export default Profile;
